/**
 * ReasoningStreamPanel — live chain-of-thought display for the
 * Multi-Agent Orchestration Studio (§4.79).
 *
 * Subscribes (via parent prop) to events of shape:
 *   { agent_id, phase: 'thinking' | 'tool_call' | 'final' | 'error', content, timestamp }
 *
 * Groups events by agent and displays them as expandable cards. Auto-scrolls
 * with pause-on-hover. The panel is docked to the right side and has a
 * collapse / clear control.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

const PHASE_META = {
  thinking: { icon: '💭', color: 'var(--warning-color)', label: 'thinking' },
  tool_call: { icon: '🔧', color: '#3b82f6', label: 'tool call' },
  final: { icon: '✅', color: 'var(--success-color)', label: 'final' },
  error: { icon: '⚠️', color: 'var(--error-color)', label: 'error' },
};

function fmtTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString();
  } catch { return ''; }
}

function renderContent(content) {
  if (content == null) return null;
  if (typeof content === 'string') return content;
  try {
    return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(content, null, 2)}</pre>;
  } catch {
    return String(content);
  }
}

export default function ReasoningStreamPanel({ events = [], nodes = [], onClose, onClear }) {
  const [collapsed, setCollapsed] = useState({});
  const scrollRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Group events per agent_id; keep order
  const grouped = useMemo(() => {
    const out = new Map();
    events.forEach(ev => {
      const k = ev.agent_id || 'unknown';
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(ev);
    });
    return Array.from(out.entries());
  }, [events]);

  const labelById = useMemo(() => {
    const map = {};
    nodes.forEach(n => {
      map[n.id] = n.data?.label || n.id;
    });
    return map;
  }, [nodes]);

  // Auto-scroll on new events unless hovered
  useEffect(() => {
    if (paused) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events, paused]);

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: '70px',
        bottom: 0,
        width: '360px',
        background: 'var(--bg-secondary)',
        borderLeft: '1px solid var(--border-color)',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1500,
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div style={{
        padding: '10px 12px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>
          🧠 Reasoning Stream
        </span>
        {paused && <span style={{ fontSize: '10px', color: 'var(--warning-color)' }}>scroll paused</span>}
        <button
          onClick={onClear}
          title="Clear events"
          style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}
        >Clear</button>
        <button
          onClick={onClose}
          title="Close panel"
          style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
        >×</button>
      </div>

      <div
        ref={scrollRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
            e.preventDefault();
            e.stopPropagation();
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }}
        style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', outline: 'none' }}
      >
        {grouped.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px', textAlign: 'center', marginTop: '20px' }}>
            No agent reasoning yet. Run the workflow to see live chain-of-thought.
          </div>
        )}
        {grouped.map(([agentId, evs]) => {
          const isCollapsed = collapsed[agentId];
          const agentLabel = labelById[agentId] || agentId;
          return (
            <div key={agentId} style={{
              marginBottom: '10px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              background: 'var(--bg-tertiary, var(--bg-primary))',
            }}>
              <div
                onClick={() => setCollapsed(c => ({ ...c, [agentId]: !c[agentId] }))}
                style={{
                  padding: '8px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  borderBottom: isCollapsed ? 'none' : '1px solid var(--border-color)',
                }}
              >
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                  {isCollapsed ? '▶' : '▼'}
                </span>
                <span style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)', flex: 1 }}>
                  {agentLabel}
                </span>
                <span style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', padding: '1px 6px', borderRadius: '8px', fontSize: '10px' }}>
                  {evs.length}
                </span>
              </div>
              {!isCollapsed && (
                <div style={{ padding: '6px 10px' }}>
                  {evs.map((ev, idx) => {
                    const meta = PHASE_META[ev.phase] || { icon: '•', color: 'var(--text-tertiary)', label: ev.phase || 'event' };
                    return (
                      <div key={idx} style={{ marginBottom: '6px', borderLeft: `3px solid ${meta.color}`, paddingLeft: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: meta.color, fontWeight: 600 }}>
                          <span>{meta.icon}</span>
                          <span style={{ textTransform: 'uppercase' }}>{meta.label}</span>
                          <span style={{ marginLeft: 'auto', color: 'var(--text-tertiary)', fontWeight: 400 }}>{fmtTime(ev.timestamp)}</span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-primary)', marginTop: '2px', wordBreak: 'break-word' }}>
                          {renderContent(ev.content)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
